import { useEffect, useState } from "react";
import { Download, X, Share, Plus } from "lucide-react";
import { useLang } from "@/i18n/LangContext";

const DISMISS_KEY = "pwa_install_dismissed_at";
const DISMISS_TTL_MS = 1000 * 60 * 60 * 24 * 14; // 14 days

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

function isIos() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

export default function PwaInstallPrompt() {
  const { t } = useLang();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosVisible, setIosVisible] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    let dismissed = 0;
    try {
      dismissed = Number(localStorage.getItem(DISMISS_KEY) || 0);
    } catch (e) {
      dismissed = 0;
    }
    const isFresh = Date.now() - dismissed < DISMISS_TTL_MS;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (!isFresh) setVisible(true);
    };
    const onInstalled = () => {
      setVisible(false);
      setIosVisible(false);
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari has no beforeinstallprompt - show manual instructions
    if (isIos() && !isFresh) {
      const tmo = setTimeout(() => setIosVisible(true), 4000);
      return () => {
        clearTimeout(tmo);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
        window.removeEventListener("appinstalled", onInstalled);
      };
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    setIosVisible(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch (e) {
      /* storage unavailable */
    }
  };

  const install = async () => {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome === "accepted") {
        setVisible(false);
      } else {
        dismiss();
      }
    } catch (e) {
      dismiss();
    } finally {
      setDeferredPrompt(null);
    }
  };

  if (!visible && !iosVisible) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] w-[min(92vw,420px)] rounded-2xl border border-white/15 bg-[#0b1224]/95 shadow-2xl backdrop-blur-md"
    >
      <div className="flex items-start gap-3 p-4">
        <div className="shrink-0 w-11 h-11 rounded-xl bg-amber-400 text-[#040914] flex items-center justify-center font-extrabold text-lg">
          PR
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white">
            {t("Zainstaluj Project Ruda Śląska")}
          </div>
          {visible ? (
            <p className="mt-1 text-xs text-white/70 leading-snug">
              {t(
                "Dodaj stronę do ekranu głównego - szybsze ładowanie, tryb offline i własna ikona aplikacji."
              )}
            </p>
          ) : (
            <p className="mt-1 text-xs text-white/70 leading-snug flex items-center gap-1 flex-wrap">
              {t("Aby zainstalować, dotknij")}
              <Share className="w-3.5 h-3.5 inline" />
              {t("i wybierz")}
              <span className="inline-flex items-center gap-1 px-1 rounded bg-white/10">
                <Plus className="w-3 h-3" /> {t("Do ekranu początkowego")}
              </span>
            </p>
          )}
          {visible && (
            <div className="mt-3 flex gap-2">
              <button
                data-testid="pwa-install-btn"
                onClick={install}
                className="inline-flex items-center gap-2 rounded-lg bg-amber-400 hover:bg-amber-300 text-[#040914] text-xs font-semibold px-3 py-2 transition"
              >
                <Download className="w-4 h-4" />
                {t("Zainstaluj")}
              </button>
              <button
                onClick={dismiss}
                className="text-xs text-white/60 hover:text-white px-3 py-2"
              >
                {t("Może później")}
              </button>
            </div>
          )}
        </div>
        <button
          aria-label="Zamknij"
          onClick={dismiss}
          className="shrink-0 text-white/50 hover:text-white"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
